'use client';

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addressesApi, type SavedAddress, type SavedAddressInput } from '@/lib/api/addresses';
import { useAuthStore, selectIsAuthenticatedSafe } from '@/stores/auth-store';

export const ADDRESS_BOOK_QUERY_KEY = ['address-book'] as const;

/**
 * Адресная книга пользователя.
 *
 * Запрос уходит ТОЛЬКО авторизованным: у гостя ручка отдаст 401, и каждый заход
 * в корзину добавлял бы красную строку в лог ошибок ни за что.
 *
 * Сбой запроса — это пустая книга, а не ошибка на экране: адрес всегда можно
 * ввести руками, и оформление заказа от книги не зависит.
 */
export function useAddressBook() {
  // Safe-селектор: до гидрации persist'а он false, поэтому список адресов не
  // появляется в первом кадре и не ломает разметку рассинхроном SSR/CSR.
  const isAuthenticated = useAuthStore(selectIsAuthenticatedSafe);

  const query = useQuery({
    queryKey: ADDRESS_BOOK_QUERY_KEY,
    queryFn: addressesApi.list,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  return {
    addresses: query.data ?? [],
    isLoading: query.isLoading && isAuthenticated,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useAddressBookMutations() {
  const queryClient = useQueryClient();

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ADDRESS_BOOK_QUERY_KEY });
  }, [queryClient]);

  const create = useMutation({
    mutationFn: (input: SavedAddressInput) => addressesApi.create(input),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: number; input: SavedAddressInput }) =>
      addressesApi.update(id, input),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: number) => addressesApi.remove(id),
    onSuccess: invalidate,
  });

  return { create, update, remove, invalidate };
}

/** Адрес, который подставляем по умолчанию: помеченный основным, иначе первый. */
export function pickDefaultAddress(addresses: SavedAddress[]): SavedAddress | null {
  if (addresses.length === 0) return null;
  return addresses.find((item) => item.is_default === 1) ?? addresses[0];
}
